/**
 * Plugin realtime-subscription manager (ADR-012 Phase M4) — the host side of
 * subscribe-kind entity verbs.
 *
 * Lives in its OWN module (not PluginBridge.ts) because the bridge-op-freeze
 * and bridge-permission-coverage architecture tests regex-scan every
 * `case '<snake_case>':` label in PluginBridge.ts — so this module uses
 * if/else dispatch only, and the bridge wires it in without new case labels.
 *
 * ENTITY-AGNOSTIC by doctrine: nothing here knows about datastores. The
 * manager recognizes subscribe handshakes purely by the typed envelope shape
 * (`{kind: 'subscription', …}` — server-Zod-validated at the source; the
 * guard below re-checks the schema's meaningful constraints since the client
 * bundle carries no zod), and delivers typed invalidations; what to refetch
 * is the SDK data-class hook's business (transport/policy split).
 *
 * Logical vs physical: each iframe consumer holds a HOST-MINTED logical
 * subscription id; one physical record per topic owns the wire payload,
 * grant/lease lifecycle, and renewal timer. N components observing one
 * collection cost one socket topic. The server envelope's subscriptionId is
 * grant-binding/audit correlation only and never reaches the iframe — nor
 * do the grant or topic.
 *
 * Lease lifecycle: the envelope's `grantExpiresAt` bounds only the ack wait;
 * the lease exists once `topic_subscribed` returns the authoritative
 * `leaseExpiresAt`. ADMISSION IS TRACKED PER ATTEMPT: every grant
 * presentation records a pending attempt that only the matching ack/denial/
 * timeout settles — so a lost renewal ack retries even while the old lease
 * is still live, and a stale denial after a newer ack is ignored. The
 * subscribe handshake RESOLVES TO THE IFRAME ONLY AFTER ADMISSION, which is
 * what makes the SDK's subscribe-then-fetch ordering truthful. Renewal
 * re-runs the stored wire payload (fresh authorization + audit row
 * server-side) ahead of expiry — the OLD lease (and, on a topic change, the
 * old SUBSCRIPTION) stays live until the replacement is admitted, so a
 * failed renewal degrades to retries-until-real-expiry, never a premature
 * teardown. Definitive authorization failures tear down immediately.
 */

interface SubscriptionEnvelope {
  kind: 'subscription';
  subscriptionId: string;
  topic: string;
  grant: string;
  grantExpiresAt: number;
}

/**
 * Structural slice of FiasWebSocket the manager uses — kept minimal so unit
 * tests can stub it without a socket.
 *
 * NOTE: FiasWebSocket fires `onReconnect` callbacks on EVERY successful
 * open, INCLUDING the initial connection this manager itself initiates
 * (and it fires them before the awaited `connect()` resolves). The
 * restoration path below is guarded against that: it only re-handshakes
 * subscriptions that have already been admitted once and have no admission
 * attempt in flight.
 */
export interface SubscriptionSocket {
  getState(): string;
  connect(tokenProvider: () => Promise<string | null>): Promise<void>;
  topicSubscribe(topic: string, grant: string): void;
  topicUnsubscribe(topic: string): void;
  onTopicSubscribed(cb: (topic: string, leaseExpiresAt: number) => void): () => void;
  onTopicSubscribeDenied(cb: (topic: string, code: 'DENIED' | 'LIMIT') => void): () => void;
  onSubscriptionEvent(
    cb: (topic: string, event: { collection: string; changed: true }) => void,
  ): () => void;
  onSubscriptionExpired(cb: (topic: string) => void): () => void;
  onTopicTerminated(cb: (topic: string) => void): () => void;
  onReconnect(cb: () => void): () => void;
}

export interface PluginSubscriptionManagerOptions {
  /**
   * Host-side re-invocation of a stored entity_invoke wire payload —
   * renewal and reconnect re-handshakes go through this, DELIBERATELY
   * bypassing the iframe-facing client rate limiter (they are
   * host-initiated; the server's per-op bucket is authoritative). Must
   * reject with an Error carrying a `code` property for server rejections.
   */
  invoke: (payload: Record<string, unknown>) => Promise<unknown>;
  /** The app's singleton socket. */
  socket: SubscriptionSocket;
  /** Mints a fresh single-use WS ticket (host session — never the iframe). */
  getSocketTicket: () => Promise<string | null>;
  /** Posts a message into the plugin iframe. */
  sendToPlugin: (message: Record<string, unknown>) => void;
}

/** Closed reason union — the SDK distinguishes terminal from retryable. */
export type SubscriptionEndedReason =
  | 'authorization_revoked'
  | 'lease_expired'
  | 'collection_deleted'
  | 'limit'
  | 'transport_error'
  | 'unsubscribed';

/** Renewal fires this long before the authoritative lease end (minus jitter). */
const RENEWAL_LEAD_MS = 90_000;
const RENEWAL_JITTER_MS = 15_000;
/** Trailing-edge delivery debounce per topic (matches the server window). */
const EVENT_DEBOUNCE_MS = 250;
/** Transient-failure retry: bounded exponential backoff. */
const RETRY_BASE_MS = 2_000;
const RETRY_MAX_MS = 30_000;
const MAX_CONSECUTIVE_RETRIES = 5;
/**
 * Socket denials are COARSE by design (the server hides why — invalid grant,
 * binding mismatch, or per-topic cap all read `DENIED`), so a permanent
 * denial cannot be told from a transient one. Each denial burns extra retry
 * budget so a permanently denied topic exhausts after this many
 * re-handshakes instead of the full transient budget — bounding the audited
 * handshake churn without leaking denial semantics to the iframe.
 */
const MAX_DENIAL_RETRIES = 2;

/** Server codes that mean "re-handshaking cannot help" — terminal. */
const TERMINAL_AUTHZ_CODES = new Set([
  'PERMISSION_DENIED',
  'FORBIDDEN',
  'NOT_A_MEMBER',
  'WORKSPACE_NOT_FOUND',
  'WORKSPACE_REQUIRED',
  // A revoked (or never-given) workspace arche approval: re-handshaking
  // cannot help until an admin approves again (ADR-022).
  'ARCHE_NOT_APPROVED',
]);
const TERMINAL_GONE_CODES = new Set(['COLLECTION_NOT_FOUND']);

const TOPIC_GRAMMAR_RE = /^[A-Za-z0-9_-]+(?::[A-Za-z0-9_-]+)*$/;

interface PendingAttempt {
  id: number;
  /**
   * The shared admission outcome — EVERY interested party (concurrent
   * initial consumers, renewals, restoration pacing) awaits this one
   * promise. One resolve/reject fans out to all waiters.
   */
  promise: Promise<void>;
  resolve: () => void;
  reject: (err: Error) => void;
}

interface PhysicalSubscription {
  topic: string;
  /** The entity_invoke wire payload replayed on renewal/reconnect. */
  wirePayload: Record<string, unknown>;
  /** Authoritative lease end from the latest accepted ack; 0 = none yet. */
  leaseExpiresAt: number;
  logicalIds: Set<string>;
  /**
   * The in-flight grant presentation, if any. INVARIANT: at most ONE
   * outstanding admission per topic — a presentation while one is pending
   * JOINS it (its grant is discarded) instead of superseding it. That is
   * what lets concurrent initial consumers share one admission, and what
   * makes topic-keyed acks/denials unambiguous client-side without
   * protocol-level attempt ids (the retry backoff ≥2s drains any late frame
   * from a timed-out grant before the next presentation). Settled ONLY by
   * the ack, a denial, or the grant-bounded ack timeout — never by the age
   * of a previous lease (a lost renewal ack must retry even while the old
   * lease is live).
   */
  pendingAttempt?: PendingAttempt;
  attemptCounter: number;
  /**
   * Set on a STAGED SUCCESSOR during a changed-topic renewal: the old topic
   * whose logical subscribers this record adopts — but only at admission.
   * Until then the old physical (and its lease) stays fully live.
   */
  adoptFrom?: string;
  renewTimer?: ReturnType<typeof setTimeout>;
  ackTimer?: ReturnType<typeof setTimeout>;
  retryTimer?: ReturnType<typeof setTimeout>;
  debounceTimer?: ReturnType<typeof setTimeout>;
  pendingEvent?: { collection: string; changed: true };
  consecutiveRetries: number;
  /** Guards against overlapping renewals (single-flight per topic). */
  renewing: boolean;
}

/**
 * Client-side re-check of the server envelope schema's meaningful
 * constraints (the bundle has no zod): exact literal kind, bounded
 * non-empty strings, the grant token format, the topic grammar, and a
 * finite positive integer `grantExpiresAt`. Deliberately NOT checked:
 * `grantExpiresAt` being in the future — client clock skew would turn a
 * valid envelope into a rejection; the ack timeout already bounds a stale
 * grant's blast radius.
 */
export function isSubscriptionEnvelope(value: unknown): value is SubscriptionEnvelope {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    v.kind === 'subscription' &&
    typeof v.subscriptionId === 'string' &&
    v.subscriptionId.length > 0 &&
    v.subscriptionId.length <= 64 &&
    typeof v.topic === 'string' &&
    v.topic.length > 0 &&
    v.topic.length <= 256 &&
    TOPIC_GRAMMAR_RE.test(v.topic) &&
    typeof v.grant === 'string' &&
    /^fias_sgr_[a-f0-9]{64}$/.test(v.grant) &&
    typeof v.grantExpiresAt === 'number' &&
    Number.isSafeInteger(v.grantExpiresAt) &&
    v.grantExpiresAt > 0
  );
}

export class PluginSubscriptionManager {
  private readonly options: PluginSubscriptionManagerOptions;
  private readonly physicalByTopic = new Map<string, PhysicalSubscription>();
  private readonly logicalToTopic = new Map<string, string>();
  private socketListenersAttached = false;
  private socketUnsubscribers: Array<() => void> = [];
  private logicalCounter = 0;
  private destroyed = false;

  constructor(options: PluginSubscriptionManagerOptions) {
    this.options = options;
  }

  /**
   * Inspects an entity_invoke response body. Non-subscription responses pass
   * through untouched. A subscription envelope is CONSUMED here: the manager
   * takes over grant/topic/lease, performs the socket subscribe, and — ONLY
   * AFTER THE SOCKET ADMITS IT (`topic_subscribed`) — resolves with
   * `{kind: 'subscription', subscriptionId: <logical>}` for the iframe.
   * Waiting for admission is load-bearing: the SDK refetches when this
   * resolves, and the subscribe-then-fetch guarantee (a write racing the
   * handshake is never missed) only holds if the subscription is live first.
   * A denial or grant-expiry timeout REJECTS, and the failed logical
   * subscription is cleaned up.
   *
   * `wirePayload` is the entity_invoke payload that minted this response —
   * stored so renewals and reconnect restoration can replay the handshake
   * (fresh authorization every time).
   */
  async handleInvokeResponse(
    body: unknown,
    wirePayload: Record<string, unknown>,
  ): Promise<unknown> {
    const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
    const envelope = record && isSubscriptionEnvelope(record.output) ? record.output : null;
    if (!envelope || this.destroyed) return body;

    this.attachSocketListeners();

    const logicalId = `lsub_${++this.logicalCounter}`;
    const existing = this.physicalByTopic.get(envelope.topic);
    if (existing && existing.leaseExpiresAt > Date.now() && !existing.pendingAttempt) {
      // Refcount onto an ALREADY-ADMITTED topic: the subscription is live,
      // so the iframe response is truthful immediately. The fresh grant is
      // still presented — an idempotent re-subscribe extends the lease —
      // but its outcome only matters to the renewal machinery.
      existing.logicalIds.add(logicalId);
      this.logicalToTopic.set(logicalId, envelope.topic);
      this.presentGrant(existing, envelope).catch(() => {
        // Extension failure: the live lease is untouched; the attempt's
        // failure path has already scheduled recovery.
      });
      return { ...record, output: { kind: 'subscription', subscriptionId: logicalId } };
    }

    const physical: PhysicalSubscription = existing ?? {
      topic: envelope.topic,
      wirePayload,
      leaseExpiresAt: 0,
      logicalIds: new Set(),
      attemptCounter: 0,
      consecutiveRetries: 0,
      renewing: false,
    };
    this.physicalByTopic.set(envelope.topic, physical);
    physical.logicalIds.add(logicalId);
    this.logicalToTopic.set(logicalId, envelope.topic);

    try {
      // Concurrent initial consumers (the two-components-on-mount case)
      // land here while the first admission is still pending — presentGrant
      // JOINS the in-flight admission (this consumer's own grant is
      // discarded; it was only ever an admission credential) so every
      // waiter shares ONE outcome instead of superseding each other.
      await this.presentGrant(physical, envelope);
    } catch (err) {
      // Admission failed — undo THIS consumer's registration. Every
      // concurrent waiter sees the same rejection and does the same; the
      // physical record drops when the last one leaves (which also clears
      // any recovery timer the failure path armed).
      physical.logicalIds.delete(logicalId);
      this.logicalToTopic.delete(logicalId);
      if (physical.logicalIds.size === 0) {
        this.dropPhysical(physical, /* unsubscribeSocket */ false);
      }
      throw err instanceof Error ? err : new Error('Subscription was not admitted');
    }

    return {
      ...record,
      output: { kind: 'subscription', subscriptionId: logicalId },
    };
  }

  /** Iframe-initiated release of one logical subscription. */
  release(logicalId: string): void {
    const topic = this.logicalToTopic.get(logicalId);
    if (!topic) return;
    this.logicalToTopic.delete(logicalId);
    const physical = this.physicalByTopic.get(topic);
    if (!physical) return;
    physical.logicalIds.delete(logicalId);
    if (physical.logicalIds.size === 0) {
      this.dropPhysical(physical, /* unsubscribeSocket */ true);
    }
  }

  /** Host teardown — the pendingImageRequests cleanup contract. */
  destroy(): void {
    this.destroyed = true;
    for (const physical of Array.from(this.physicalByTopic.values())) {
      this.dropPhysical(physical, /* unsubscribeSocket */ true);
    }
    this.logicalToTopic.clear();
    for (const unsubscribe of this.socketUnsubscribers) unsubscribe();
    this.socketUnsubscribers = [];
    this.socketListenersAttached = false;
  }

  // ==========================================================================
  // Internals
  // ==========================================================================

  private attachSocketListeners(): void {
    if (this.socketListenersAttached) return;
    this.socketListenersAttached = true;
    const socket = this.options.socket;
    this.socketUnsubscribers = [
      socket.onTopicSubscribed((topic, leaseExpiresAt) => this.onAck(topic, leaseExpiresAt)),
      socket.onTopicSubscribeDenied((topic, code) => this.onDenied(topic, code)),
      socket.onSubscriptionEvent((topic, event) => this.onEvent(topic, event)),
      socket.onSubscriptionExpired((topic) => this.onExpired(topic)),
      socket.onTopicTerminated((topic) => this.onTerminated(topic)),
      socket.onReconnect(() => void this.restoreAll()),
    ];
  }

  /**
   * Connects (if needed) and presents a grant. Resolves when the MATCHING
   * `topic_subscribed` ack lands; rejects on denial or when the grant's
   * admission window elapses unacknowledged. The rejection path has already
   * routed recovery (retry scheduling / successor rollback) before the
   * caller sees it.
   *
   * SERIALIZED per topic: while an admission is pending, further
   * presentations JOIN it — the caller awaits the in-flight outcome and the
   * new grant is discarded unused (it expires server-side in 30s). This
   * keeps at most one unsettled ack/denial per topic in flight, so
   * topic-keyed frames cannot be misattributed across attempts.
   */
  private presentGrant(
    physical: PhysicalSubscription,
    envelope: SubscriptionEnvelope,
  ): Promise<void> {
    if (physical.pendingAttempt) {
      return physical.pendingAttempt.promise;
    }

    const attemptId = ++physical.attemptCounter;
    let resolveAdmission!: () => void;
    let rejectAdmission!: (err: Error) => void;
    const admission = new Promise<void>((resolve, reject) => {
      resolveAdmission = resolve;
      rejectAdmission = reject;
    });
    physical.pendingAttempt = {
      id: attemptId,
      promise: admission,
      resolve: resolveAdmission,
      reject: rejectAdmission,
    };
    // A rejected admission is always routed through failure handling below;
    // callers that don't await (lease extensions) must not trigger
    // unhandled-rejection noise.
    admission.catch(() => {});

    void (async () => {
      const socket = this.options.socket;
      try {
        if (socket.getState() !== 'connected') {
          // NOTE: the socket fires onReconnect during THIS connect (initial
          // open included) — restoreAll() is guarded so a never-admitted
          // physical with a pending attempt is not double-handshaked.
          await socket.connect(this.options.getSocketTicket);
        }
      } catch {
        this.settleAttemptFailure(physical, attemptId, 'transport_error');
        return;
      }
      if (this.destroyed || !this.physicalByTopic.has(physical.topic)) {
        this.settleAttemptFailure(physical, attemptId, 'transport_error');
        return;
      }
      if (physical.pendingAttempt?.id !== attemptId) return; // superseded meanwhile

      socket.topicSubscribe(physical.topic, envelope.grant);

      // Ack timeout bounded by the grant's admission window. Keyed on THIS
      // attempt: a still-live OLD lease must not mask a lost renewal ack.
      this.clearTimer(physical, 'ackTimer');
      const waitMs = Math.max(1_000, envelope.grantExpiresAt - Date.now() + 1_000);
      physical.ackTimer = setTimeout(() => {
        if (physical.pendingAttempt?.id !== attemptId) return; // already settled
        this.settleAttemptFailure(physical, attemptId, 'transport_error');
      }, waitMs);
    })();

    return admission;
  }

  /** Rejects the given attempt (if still pending) and routes recovery. */
  private settleAttemptFailure(
    physical: PhysicalSubscription,
    attemptId: number,
    reason: SubscriptionEndedReason,
  ): void {
    if (physical.pendingAttempt?.id !== attemptId) return;
    const attempt = physical.pendingAttempt;
    physical.pendingAttempt = undefined;
    this.clearTimer(physical, 'ackTimer');
    attempt.reject(new Error(`subscription admission failed: ${reason}`));
    this.handleAdmissionFailure(physical, reason);
  }

  private onAck(topic: string, leaseExpiresAt: number): void {
    const physical = this.physicalByTopic.get(topic);
    if (!physical) return;
    physical.leaseExpiresAt = leaseExpiresAt;
    physical.consecutiveRetries = 0;
    physical.renewing = false;
    this.clearTimer(physical, 'ackTimer');
    this.clearTimer(physical, 'retryTimer');
    const attempt = physical.pendingAttempt;
    physical.pendingAttempt = undefined;
    attempt?.resolve();

    // Staged successor admitted (changed-topic renewal): NOW adopt the old
    // topic's logical subscribers and tear the old subscription down —
    // never before admission, so a failed successor leaves the old fully
    // live (iframe ids stay stable throughout).
    if (physical.adoptFrom) {
      const old = this.physicalByTopic.get(physical.adoptFrom);
      physical.adoptFrom = undefined;
      if (old && old !== physical) {
        this.migrateLogicals(old, physical);
      }
    }

    // Proactive renewal ahead of the authoritative lease end (jittered so a
    // full-cap connection's renewals don't bunch).
    this.clearTimer(physical, 'renewTimer');
    const delay = Math.max(
      1_000,
      leaseExpiresAt - Date.now() - RENEWAL_LEAD_MS - Math.random() * RENEWAL_JITTER_MS,
    );
    physical.renewTimer = setTimeout(() => void this.renew(physical), delay);
  }

  private onDenied(topic: string, code: 'DENIED' | 'LIMIT'): void {
    const physical = this.physicalByTopic.get(topic);
    if (!physical) return;
    // A denial with no attempt in flight is STALE (a newer ack already
    // settled this topic) — ignore it rather than scheduling spurious work.
    const attempt = physical.pendingAttempt;
    if (!attempt) return;
    if (code === 'DENIED') {
      // Coarse denial: burn extra budget so a permanent denial exhausts
      // after MAX_DENIAL_RETRIES re-handshakes (see the constant's note).
      physical.consecutiveRetries = Math.max(
        physical.consecutiveRetries,
        MAX_CONSECUTIVE_RETRIES - MAX_DENIAL_RETRIES,
      );
    }
    this.settleAttemptFailure(physical, attempt.id, code === 'LIMIT' ? 'limit' : 'transport_error');
  }

  /**
   * Moves every logical subscriber from `from` onto `to` (iframe ids stay
   * stable) and tears the source subscription down. Used at successor
   * admission and when a changed-topic renewal lands on a topic another
   * consumer already holds.
   */
  private migrateLogicals(from: PhysicalSubscription, to: PhysicalSubscription): void {
    for (const logicalId of from.logicalIds) {
      to.logicalIds.add(logicalId);
      this.logicalToTopic.set(logicalId, to.topic);
    }
    from.logicalIds.clear();
    from.renewing = false;
    this.dropPhysical(from, /* unsubscribeSocket */ true);
  }

  /**
   * Recovery routing for a failed admission attempt. A staged successor
   * rolls back (the OLD subscription is still live and keeps its own
   * recovery); anything else enters the bounded retry loop.
   */
  private handleAdmissionFailure(
    physical: PhysicalSubscription,
    reason: SubscriptionEndedReason,
  ): void {
    if (physical.adoptFrom) {
      const old = this.physicalByTopic.get(physical.adoptFrom);
      this.dropPhysical(physical, /* unsubscribeSocket */ true);
      if (old) {
        old.renewing = false;
        this.scheduleRetry(old, reason);
      }
      return;
    }
    physical.renewing = false;
    this.scheduleRetry(physical, reason);
  }

  private onEvent(topic: string, event: { collection: string; changed: true }): void {
    const physical = this.physicalByTopic.get(topic);
    if (!physical) return;
    // Trailing-edge debounce: deliver now if idle, else coalesce to one
    // trailing delivery (invalidations are idempotent).
    if (physical.debounceTimer) {
      physical.pendingEvent = event;
      return;
    }
    this.deliver(physical, event);
    physical.debounceTimer = setTimeout(() => {
      physical.debounceTimer = undefined;
      const pending = physical.pendingEvent;
      physical.pendingEvent = undefined;
      if (pending) this.deliver(physical, pending);
    }, EVENT_DEBOUNCE_MS);
  }

  private deliver(
    physical: PhysicalSubscription,
    event: { collection: string; changed: true },
  ): void {
    for (const logicalId of physical.logicalIds) {
      this.options.sendToPlugin({ type: 'subscription_event', subscriptionId: logicalId, event });
    }
  }

  private onExpired(topic: string): void {
    const physical = this.physicalByTopic.get(topic);
    if (!physical) return;
    // Stale frame from an older lease? If a newer accepted lease is live,
    // ignore — the race between a sweep and a renewal ack resolves in favor
    // of the newer lease. (An in-flight attempt has its own timeout.)
    if (physical.leaseExpiresAt > Date.now()) return;
    if (physical.pendingAttempt) return;
    void this.renew(physical);
  }

  private onTerminated(topic: string): void {
    const physical = this.physicalByTopic.get(topic);
    if (!physical) return;
    // Terminal by definition (the resource is gone) — no re-handshake.
    this.endPhysical(physical, 'collection_deleted', /* unsubscribeSocket */ false);
  }

  /**
   * Re-runs the stored handshake for one physical subscription and awaits
   * ADMISSION (not just the handshake) — restoration paces on this.
   * Single-flight per topic; the old lease/subscription stays live until
   * the replacement is admitted.
   */
  private async renew(physical: PhysicalSubscription): Promise<void> {
    if (this.destroyed || physical.renewing || !this.physicalByTopic.has(physical.topic)) return;
    physical.renewing = true;
    try {
      const body = (await this.options.invoke(physical.wirePayload)) as Record<string, unknown>;
      const envelope = isSubscriptionEnvelope(body?.output) ? body.output : null;
      if (!envelope) {
        physical.renewing = false;
        this.scheduleRetry(physical, 'transport_error');
        return;
      }
      if (envelope.topic === physical.topic) {
        // presentGrant's failure path routes recovery; renewing clears on
        // ack (onAck) or failure (handleAdmissionFailure).
        await this.presentGrant(physical, envelope).catch(() => {});
        return;
      }
      // The authorized topic CHANGED (e.g. the collection was recreated —
      // new incarnation id).
      const collision = this.physicalByTopic.get(envelope.topic);
      if (collision) {
        // Another consumer already holds the successor topic (subscribed to
        // the recreated collection before this renewal resolved). NEVER
        // overwrite its record — its lease, timers, and logical subscribers
        // are live state. Instead: join its admission if one is pending
        // (or present our grant as an extension if it is already admitted),
        // and on success migrate this record's logical subscribers onto it.
        try {
          await this.presentGrant(collision, envelope);
          this.migrateLogicals(physical, collision);
        } catch {
          // The collision's own failure path owns its recovery; this old
          // subscription stays live and retries later.
          physical.renewing = false;
          this.scheduleRetry(physical, 'transport_error');
        }
        return;
      }
      // STAGE a fresh successor: the old subscription (and its lease,
      // timers, logical mappings) stays fully live until the successor is
      // ADMITTED — adoption happens in onAck; a failed successor rolls back
      // in handleAdmissionFailure.
      const successor: PhysicalSubscription = {
        topic: envelope.topic,
        wirePayload: physical.wirePayload,
        leaseExpiresAt: 0,
        logicalIds: new Set(),
        attemptCounter: 0,
        consecutiveRetries: 0,
        renewing: false,
        adoptFrom: physical.topic,
      };
      this.physicalByTopic.set(envelope.topic, successor);
      await this.presentGrant(successor, envelope).catch(() => {});
    } catch (error) {
      physical.renewing = false;
      const code = (error as { code?: string }).code;
      if (code && TERMINAL_GONE_CODES.has(code)) {
        this.endPhysical(physical, 'collection_deleted', true);
        return;
      }
      if (code && TERMINAL_AUTHZ_CODES.has(code)) {
        this.endPhysical(physical, 'authorization_revoked', true);
        return;
      }
      this.scheduleRetry(physical, 'transport_error');
    }
  }

  /**
   * Bounded-backoff retry of the full handshake. If the lease has already
   * truly expired and the budget is spent, the subscription ends with the
   * reason that got us here.
   */
  private scheduleRetry(physical: PhysicalSubscription, reason: SubscriptionEndedReason): void {
    if (this.destroyed || !this.physicalByTopic.has(physical.topic)) return;
    if (physical.consecutiveRetries >= MAX_CONSECUTIVE_RETRIES) {
      const finalReason: SubscriptionEndedReason =
        physical.leaseExpiresAt > 0 && physical.leaseExpiresAt <= Date.now()
          ? 'lease_expired'
          : reason;
      this.endPhysical(physical, finalReason, true);
      return;
    }
    physical.consecutiveRetries += 1;
    const backoff = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (physical.consecutiveRetries - 1));
    const delay = backoff + Math.random() * 1_000;
    this.clearTimer(physical, 'retryTimer');
    physical.retryTimer = setTimeout(() => void this.renew(physical), delay);
  }

  /**
   * Reconnect: every ADMITTED topic needs a fresh handshake (grants are
   * single-use — the server-side subscription state died with the socket).
   *
   * Two guards keep this from double-handshaking:
   *  - `leaseExpiresAt === 0` — never admitted: its ORIGINAL handshake is
   *    still in flight (the socket fires reconnect callbacks during the
   *    very connect() that handshake initiated, initial open included).
   *  - `pendingAttempt` — an admission is already being presented.
   *
   * Sequential AND admission-paced on purpose: each grant is minted right
   * before it is presented and acknowledged before the next mint, so
   * restoration cannot outrun the 30s grant windows or burst the frame
   * budget even with a full-cap connection.
   */
  private async restoreAll(): Promise<void> {
    for (const physical of Array.from(this.physicalByTopic.values())) {
      if (this.destroyed) return;
      if (physical.pendingAttempt || physical.renewing) continue;
      // A never-admitted record in failure backoff belongs to its retry
      // timer. A never-admitted record with NEITHER an attempt nor a retry
      // pending is an orphan (should be unreachable) — renew it rather than
      // skip it forever.
      if (physical.leaseExpiresAt === 0 && physical.retryTimer) continue;
      await this.renew(physical);
    }
  }

  private endPhysical(
    physical: PhysicalSubscription,
    reason: SubscriptionEndedReason,
    unsubscribeSocket: boolean,
  ): void {
    this.clearAllTimers(physical);
    physical.pendingAttempt?.reject(new Error(`subscription ended: ${reason}`));
    physical.pendingAttempt = undefined;
    this.physicalByTopic.delete(physical.topic);
    for (const logicalId of physical.logicalIds) {
      this.logicalToTopic.delete(logicalId);
      this.options.sendToPlugin({ type: 'subscription_ended', subscriptionId: logicalId, reason });
    }
    if (unsubscribeSocket) {
      this.options.socket.topicUnsubscribe(physical.topic);
    }
  }

  private dropPhysical(physical: PhysicalSubscription, unsubscribeSocket: boolean): void {
    this.clearAllTimers(physical);
    physical.pendingAttempt?.reject(new Error('subscription dropped'));
    physical.pendingAttempt = undefined;
    this.physicalByTopic.delete(physical.topic);
    for (const logicalId of physical.logicalIds) {
      this.logicalToTopic.delete(logicalId);
    }
    if (unsubscribeSocket) {
      this.options.socket.topicUnsubscribe(physical.topic);
    }
  }

  private clearTimer(
    physical: PhysicalSubscription,
    key: 'renewTimer' | 'ackTimer' | 'retryTimer' | 'debounceTimer',
  ): void {
    const timer = physical[key];
    if (timer) {
      clearTimeout(timer);
      physical[key] = undefined;
    }
  }

  private clearAllTimers(physical: PhysicalSubscription): void {
    this.clearTimer(physical, 'renewTimer');
    this.clearTimer(physical, 'ackTimer');
    this.clearTimer(physical, 'retryTimer');
    this.clearTimer(physical, 'debounceTimer');
    physical.pendingEvent = undefined;
  }
}
